import { useEffect, useRef } from 'react';
import { Dialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { welcomeFor, welcomeIsNews, welcomeParagraphs } from '../../domain/welcome';
import type { Lang } from '../../domain/types';
import { translate, useI18n } from '../../i18n';
import { useRepos } from '../../repositories';
import { useApp } from '../../app/AppState';

/**
 * The welcome message, up on its board.
 *
 * Jay: *"when you first log on to the app, there'll be a sort of billboard that
 * appears right in front of you … users could read it and then close it off
 * before continuing. After they close it, it will not appear again unless the
 * message has been changed. Then it'll appear again for everyone. If users wish
 * to see the message again, it'll be in the burger."*
 *
 * This is the one mounted in the app (see Panels). It does two things:
 *
 *   - **It brings the board up by itself when the message is news** to whoever
 *     is signed in — never read, or changed since they read it
 *     (`welcomeIsNews`). Closing it, however it is closed — the button, the ×,
 *     the backdrop — is reading it, and is stamped on their membership, so it is
 *     read once per person rather than once per phone.
 *   - **It shows the same board again when the menu asks for it**, and that
 *     reading records nothing, because there is nothing to record.
 *
 * A board that has been changed says so in a line above the words. Otherwise
 * somebody who closed "the welcome" last month is left wondering why it is back.
 */
export function WelcomeBoard() {
  const { lang } = useI18n();
  const repos = useRepos();
  const { session, family, memberships, dataReady, welcomeOpen, openWelcome, closeWelcome, reload } = useApp();

  const mine = session ? memberships.find((m) => m.accountId === session.accountId && m.status === 'active') : undefined;
  const news = !!session?.onboardingComplete && dataReady && !!family && !!mine && welcomeIsNews(family.welcome, mine.welcomeSeenAt);
  /** Which message it is: the app's own words, or the administrators' as of a moment. */
  const stamp = family?.welcome?.updatedAt ?? 'built-in';
  /**
   * What was closed in this sitting. The stamp goes to the repository and comes
   * back with the next load; in the moment between, the message still looks like
   * news, and without this the board would bounce straight back up.
   */
  const closed = useRef<string | null>(null);

  useEffect(() => {
    if (news && !welcomeOpen && closed.current !== stamp) openWelcome('news');
  }, [news, welcomeOpen, stamp, openWelcome]);

  const close = () => {
    closeWelcome();
    if (!news) return;
    closed.current = stamp;
    void repos.family.markWelcomeSeen().then(() => reload());
  };

  const shown = welcomeFor(family?.welcome, lang, { en: translate('en', 'greeting.default'), he: translate('he', 'greeting.default') });
  // Read before, and it is back: say why.
  const changed = welcomeOpen === 'news' && !!mine?.welcomeSeenAt;

  return <WelcomeSheet open={!!welcomeOpen} onClose={close} text={shown.text} textLang={shown.lang} note={changed ? 'changed' : undefined} />;
}

/**
 * The board itself — also what Admin controls opens to show an administrator
 * what they are about to put in front of everybody (`note="preview"`).
 *
 * The words are set in the language they were written in, direction included,
 * whatever language the reader's app is in: an English welcome shown to a Hebrew
 * reader (because no Hebrew one was written) still has to run left to right.
 */
export function WelcomeSheet({ open, onClose, text, textLang, note }: { open: boolean; onClose: () => void; text: string; textLang: Lang; note?: 'changed' | 'preview' }) {
  const { t } = useI18n();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('greeting.menu')}
      labelledBy="greeting-title"
      panelClassName="is-greeting"
      actions={
        <button type="button" className="btn btn-primary btn-lg btn-block" onClick={onClose}>
          <Icon name="check" />
          <span>{t(note === 'preview' ? 'nav.close' : 'greeting.continue')}</span>
        </button>
      }
    >
      <div className="greet">
        <span className="greet-crest" aria-hidden="true"><Icon name="tree" /></span>
        <h3 className="greet-title">{t('welcome.title')}</h3>
        {note && (
          <p className={`greet-note ${note === 'preview' ? 'is-preview' : ''}`}>
            <Icon name={note === 'preview' ? 'eye' : 'sparkle'} />
            <span>{t(note === 'preview' ? 'greeting.previewNote' : 'greeting.changedNote')}</span>
          </p>
        )}
        <div className="greet-words" lang={textLang} dir={textLang === 'he' ? 'rtl' : 'ltr'}>
          {welcomeParagraphs(text).map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </div>
    </Dialog>
  );
}
